import { useState, useEffect } from 'react';
import { AlertCircle, Box, RefreshCw } from 'lucide-react';
import { useSandbox } from '../hooks/useSandbox';
import { useThreadActions, useThreadsStore } from '../store/threadsStore';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import useProjectFiles from '../hooks/useProjectFiles';
import { useNavigate } from 'react-router-dom';
import ThreadsPanel from './ThreadsPanel';
import FileUpload from './FileUpload';
import { getAgentTypeLabel, getAgentTypeIcon, getAgentTypeColor } from '../utils/agentUtils';
import { HelpSystem, HelpButton } from './tour';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';

// Custom scrollbar styles for textarea
const textareaScrollbarStyles = `
  /* WebKit browsers (Chrome, Safari, Edge) */
  .textarea-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .textarea-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }

  .textarea-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.12);
    border-radius: 3px;
    transition: all 0.2s ease;
  }

  .textarea-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }

  /* Dark mode */
  .dark .textarea-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.12);
  }

  .dark .textarea-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  /* Firefox */
  .textarea-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.12) transparent;
  }

  .dark .textarea-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.12) transparent;
  }

  /* Smooth scrolling */
  .textarea-scrollbar {
    scroll-behavior: smooth;
  }

  /* Hide scrollbar when not needed */
  .textarea-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.08);
  }

  .dark .textarea-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.08);
  }
`;

// Inject styles into the document head
const injectTextareaScrollbarStyles = () => {
  if (typeof document !== 'undefined') {
    const existingStyle = document.getElementById('textarea-scrollbar-styles');
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = 'textarea-scrollbar-styles';
      style.textContent = textareaScrollbarStyles;
      document.head.appendChild(style);
    }
  }
};

const placeholderPhrases = [
  "Build a business website",
  "Create an e-commerce store",
  "Design an admin dashboard",
  "Develop a full-stack app",
  "Build a landing page",
  "Create a portfolio website",
  "Design a blog platform",
  "Build a social media app",
  "Create a task management tool",
  "Design a weather app",
  "Build a chat application",
  "Create a booking system",
  "Design a restaurant website",
  "Build a fitness tracker",
  "Create a music player",
  "Design a photo gallery",
  "Build a news aggregator",
  "Create a recipe app",
  "Design a travel planner",
  "Build a budget tracker",
  "Create a learning platform",
  "Design a marketplace",
  "Build a CRM system",
  "Create an inventory manager",
  "Design an event platform",
  "Build a real estate site",
  "Create a job board",
  "Design a forum platform",
  "Build a video streaming app",
  "Create a calendar app",
  "Design a note-taking app",
  "Build a file-sharing tool",
  "Create a survey platform",
  "Design an analytics dashboard",
  "Build a gaming platform",
  "Create a messaging app",
  "Design a healthcare app",
  "Build an education portal",
  "Create a donation platform",
  "Design an auction site",
  "Build a subscription service",
  "Create a review platform",
  "Design a delivery app",
  "Build a banking interface",
  "Create a streaming service",
  "Design a workout app",
  "Build a meditation app",
  "Create a dating platform",
  "Design a pet care app",
  "Build a smart home dashboard"
];

const ProjectHome = ({ workspaceId }) => {
  const { isReadOnly } = useReadOnlyMode();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  // Use the sandbox hook to track sandbox readiness
  const sandboxHookResult = useSandbox(workspaceId, !!workspaceId);
  const sandboxStatus = sandboxHookResult?.sandboxStatus?.status || 'idle';
  const retrySandbox = sandboxHookResult?.retry || (() => {});
  const isAutoRetrying = sandboxHookResult?.isAutoRetrying || false;
  const navigate = useNavigate();
  
  
  
  const authenticatedApi = useAuthenticatedApi();
  
  const { createThread } = useThreadActions();

  // Initialize threads store polling
  useEffect(() => {
    if (workspaceId) {
      // Start global threads polling (this will fetch threads automatically)
      const threadsStore = useThreadsStore.getState();
      threadsStore.startPolling(workspaceId, authenticatedApi);

      return () => {
        threadsStore.stopPolling();
      };
    }
  }, [workspaceId, authenticatedApi]);
  
  const {
    files: projectFiles,
    loading: filesLoading,
    error: filesError,
    uploadFile,
    uploadMultipleFiles,
    deleteFile,
    downloadFile,
    clearError: clearFilesError,
  } = useProjectFiles(workspaceId);
  


  useEffect(() => {
    // Inject scrollbar styles on component mount
    injectTextareaScrollbarStyles();
  }, []);

  useEffect(() => {
    if (isFocused || message) {
      setCurrentPlaceholder('');
      return;
    }
    
    let typeTimer;
    let pauseTimer;
    
    const cleanup = () => {
      clearInterval(typeTimer);
      clearTimeout(pauseTimer);
    };

    const startTyping = () => {
      const phrase = placeholderPhrases[placeholderIndex];
      let i = 0;
      
      typeTimer = setInterval(() => {
        setCurrentPlaceholder(phrase.slice(0, i + 1));
        i++;
        if (i >= phrase.length) {
          clearInterval(typeTimer);
          pauseTimer = setTimeout(() => {
            setPlaceholderIndex((prev) => (prev + 1) % placeholderPhrases.length);
          }, 1500);
        }
      }, 50);
    };

    const timeout = setTimeout(startTyping, 500);
    return () => {
      clearTimeout(timeout);
      cleanup();
    };
  }, [placeholderIndex, isFocused, message]);
  

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    try {
      // Create thread data with initial message but NO auto-submit
      const threadData = {
        title: 'New Code Thread',
        agent_type: 'coding_agent',
        message_history: [],
        initial_message: message.trim(),
        auto_submit_first_message: true // Enable auto-submit for ProjectHome threads
      };
      
      // Create the thread
      const createdThread = await createThread(workspaceId, threadData, authenticatedApi);
      console.log('Thread created:', createdThread); // Log the full response
      
      // Clear the message input
      setMessage('');
      
      // Navigate directly to the thread page
      if (createdThread) {
        const threadId = createdThread.id;
        console.log('Thread ID detected:', threadId); // Log the ID we're trying to use
        
        if (threadId) {
          const url = `/project/${workspaceId}/thread/${threadId}`;
          console.log('Navigating to:', url); // Debug the navigation URL
          navigate(url);
        } else {
          console.error('Created thread has no ID. Full thread object:', JSON.stringify(createdThread));
        }
      } else {
        console.error('No thread returned from createThread');
      }
    } catch (error) {
      console.error('Error creating thread:', error);
      // Could add error handling/user notification here
    }
  };
  
  return (
    <div className="flex flex-col h-full overflow-hidden bg-white dark:bg-neutral-950 text-ui-text-primary">
      
      {/* Main Content */}
      <div className="flex flex-1 h-full overflow-hidden">
        {/* ThreadsPanel Component */}
        <div className={`${sidebarOpen ? 'w-64' : 'w-14'} h-full flex-shrink-0 overflow-hidden transition-all duration-300`}>
          <ThreadsPanel
            workspaceId={workspaceId}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            navigate={navigate}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            getAgentTypeLabel={getAgentTypeLabel}
            getAgentTypeIcon={getAgentTypeIcon}
            getAgentTypeColor={getAgentTypeColor}
            data-tour="threads-panel"
          />
        </div>
        
        {/* Main chat area */}
        <main className="flex-1 flex flex-col overflow-hidden p-6">
          <div className="bg-slate-100 dark:bg-neutral-900 rounded-2xl shadow-xl border border-slate-300 dark:border-neutral-800 flex-1 flex flex-col overflow-hidden">
            {/* Centered content container */}
            <div className="flex-1 flex flex-col justify-center p-8">
              {/* Welcome message */}
              <div className="text-center mb-8" data-tour="welcome-message">
                <p className="text-neutral-900 dark:text-white text-xl font-medium tracking-tight px-4 py-2 bg-opacity-10 backdrop-blur-sm rounded-xl">What do you want to create?</p>
              </div>
              
              {/* Sandbox status indicator */}
              {sandboxStatus !== 'not_applicable' && (
                <div 
                  className="text-center mb-6 flex flex-col items-center justify-center gap-2" 
                  >
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-3 h-3 rounded-full transition-colors duration-300 ${isAutoRetrying || sandboxStatus === 'loading' ? 'bg-amber-400 animate-[pulse_1.5s_ease-in-out_infinite]' :
                      sandboxStatus === 'creating' ? 'bg-yellow-500 animate-[spin_1s_linear_infinite]' :
                      sandboxStatus === 'resuming' ? 'bg-amber-600 animate-[spin_1s_linear_infinite]' :
                      sandboxStatus === 'sleeping' ? 'bg-slate-400 animate-[pulse_3s_ease-in-out_infinite]' :
                      sandboxStatus === 'ready' ? 'bg-green-500' :
                      sandboxStatus === 'error' && !isAutoRetrying ? 'bg-red-600 hover:bg-red-500 hover:shadow-md' :
                      'bg-gray-400'}`}
                    />
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                      {isAutoRetrying ? 'Initializing your workspace' :
                      sandboxStatus === 'loading' ? 'Initializing your workspace' :
                      sandboxStatus === 'creating' ? 'Creating your workspace' :
                      sandboxStatus === 'resuming' ? 'Resuming your workspace' :
                      sandboxStatus === 'sleeping' ? 'Sandbox is sleeping' :
                      sandboxStatus === 'ready' ? 'Your workspace is ready' :
                      sandboxStatus === 'error' && !isAutoRetrying ? (
                        <span className="flex items-center gap-1">
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          Error initializing workspace
                          <button
                            onClick={retrySandbox}
                            className="text-brand-primary hover:underline ml-1 flex items-center gap-1"
                          >
                            <RefreshCw className="w-3 h-3" /> Retry
                          </button>
                        </span>
                      ) : 'Preparing your workspace...'}
                    </span>
                    {sandboxStatus === 'sleeping' && (
                      <button
                        type="button"
                        onClick={retrySandbox}
                        className="ml-2 inline-flex items-center rounded-md bg-neutral-200 dark:bg-neutral-800 px-2 py-0.5 text-xs font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-300 dark:hover:bg-neutral-700 transition-colors"
                      >
                        Wake sandbox
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Message input */}
              <div className="max-w-2xl mx-auto w-full" data-tour="message-input">
                <form onSubmit={handleMessageSubmit} className="flex items-center">
                  <div className="flex-1 rounded-xl overflow-hidden bg-ui-background-primary dark:bg-neutral-800 border border-ui-border-primary dark:border-neutral-700 focus-within:ring-2 focus-within:ring-brand-primary focus-within:border-transparent transition-all">
                    <div className="flex items-center relative w-full">
                      {!isFocused && !message && (
                        <div className="absolute left-4 right-4 top-0 bottom-0 flex items-center justify-start text-neutral-500 pointer-events-none">
                          {currentPlaceholder}
                        </div>
                      )}
                      
                      <textarea
                        value={message}
                        onChange={(e) => {
                          setMessage(e.target.value);
                          e.target.style.height = 'auto';
                          e.target.style.height = Math.min(270, Math.max(108, e.target.scrollHeight)) + 'px';
                        }}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            // Only submit if sandbox is ready and message is not empty
                            if (message.trim() !== '' && sandboxStatus === 'ready') {
                              handleMessageSubmit(e);
                            }
                          }
                        }}
                        placeholder=""
                        className="w-full resize-none focus:outline-none py-3 px-4 text-ui-text-primary dark:text-white bg-transparent border-0 min-h-[108px] placeholder-ui-text-secondary textarea-scrollbar"
                        style={{ 
                          maxHeight: '270px', 
                          overflowY: 'auto',
                        }}
                      />
                      
                      <div className="flex items-center pr-2">
                        <button
                          type="submit"
                          disabled={
                            message.trim() === '' ||
                            (sandboxStatus !== 'ready' && sandboxStatus !== 'not_applicable')
                          }
                          className={`bg-brand-action ${
                            message.trim() === '' ||
                            (sandboxStatus !== 'ready' && sandboxStatus !== 'not_applicable')
                              ? 'opacity-60'
                              : 'hover:bg-brand-action-hover'
                          } text-brand-action-text p-2.5 rounded-xl flex items-center justify-center transition-all shadow-sm`}
                          aria-label="Send message"
                          title={
                            sandboxStatus === 'sleeping'
                              ? 'Sandbox is sleeping — wake it before sending commands'
                              : sandboxStatus !== 'ready' && sandboxStatus !== 'not_applicable'
                                ? 'Waiting for workspace to be ready'
                                : 'Send message'
                          }
                          data-tour="send-button"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Project Files Section */}
              <div className="mt-8 max-w-2xl mx-auto w-full" data-tour="project-files">
                <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-slate-300 dark:border-neutral-700 shadow-sm">
                  <h3 className="font-medium text-lg mb-4 flex items-center text-neutral-900 dark:text-white">
                    <Box size={20} className="mr-2" />
                    Project Files
                  </h3>
                  <FileUpload
                    onFileUpload={uploadFile}
                    onMultipleFileUpload={uploadMultipleFiles}
                    files={projectFiles}
                    onFileDelete={isReadOnly ? () => {} : deleteFile}
                    onFileDownload={downloadFile}
                    loading={filesLoading}
                    error={filesError}
                    onClearError={clearFilesError}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      
      {/* Help System */}
      <HelpSystem pageName="projectHome" />
      <HelpButton pageName="projectHome" />
    </div>
  );
};

export default ProjectHome;
